import PropTypes from 'prop-types';
import { useHelmet, MetaTag } from '@thd-nucleus/thd-helmet';
import { extend, useDataModel } from '@thd-nucleus/data-sources';
import {
  EMT_PAGE_NAMES,
  LAST_PATH,
  OPEN_GRAPH_FACEBOOK_ADMIN,
  PAGE_TYPE,
  REVIEWS_PAGE_SIZE,
  SD_OG_IMAGE
} from '../../constants';
import { filterEmtMetadataOverrides } from '../../helpers/emt-content-helpers';
import { generateStorePageCanonical, getGraphQlReviewsOptions } from '../../helpers/pps-helpers';
import { generateTags } from '../../utils/generate-tags';
import { emtContentModel, storeReviewsModel, storeSearchModel } from '../../models';
import {
  generateStoreReviewsDesc,
  generateStoreReviewsTitle,
  getStoreReviewsSchemas
} from './store-reviews-helpers';

// --- Tag Handler ---

const storeReviewsHandler = ({
  data: { emtData, reviewData, storeData },
  props: { seoPageNumber, storeId }
}) => {
  // Extract store details data once
  const storeInfo = {
    ...storeData?.storeSearch?.stores?.[0],
    storeId
  };
  const {
    name: storeName,
    address: { city, state, postalCode } = {},
    storeDetailsPageLink
  } = storeInfo;

  const canonicalURL = generateStorePageCanonical({
    storeDetailsPageLink, urlSuffix: `${LAST_PATH.REVIEWS}/${seoPageNumber}`
  });

  // Extract store reviews data once
  const { Includes = {}, Results = [], TotalResults } = reviewData?.storeReviews || {};
  const { AverageOverallRating = 0, TotalReviewCount } = Includes?.Products?.store?.FilteredReviewStatistics || {};
  const reviewInfo = {
    AverageOverallRating,
    TotalReviewCount,
    Results
  };

  // Extract emtContent data once
  const emtContent = emtData?.emtContent?.content || {};
  const emtContentMetadata = emtContent?.reviewsMetadata || {};

  const fallbackTitle = generateStoreReviewsTitle({
    city, storeName, postalCode, state, seoPageNumber
  });
  const fallbackDescription = generateStoreReviewsDesc({ city, state, storeName });
  const {
    metaDescription = fallbackDescription,
    ogDescription = fallbackDescription,
    ogImage = SD_OG_IMAGE,
    ogTitle = fallbackTitle,
    ogType = 'PPS Review',
    schema: emtSchema,
    title = fallbackTitle
  } = filterEmtMetadataOverrides(emtContentMetadata);

  const schemas = getStoreReviewsSchemas({ emtSchema, storeInfo, reviewInfo });

  const tags = generateTags({
    canonical: canonicalURL,
    description: metaDescription,
    fbAdmins: OPEN_GRAPH_FACEBOOK_ADMIN,
    ogDescription,
    ogImage,
    ogSiteName: 'The Home Depot',
    ogTitle,
    ogType,
    ogUrl: canonicalURL,
    schemas,
    title
  });

  const totalPages = TotalResults ? Math.ceil(TotalResults / REVIEWS_PAGE_SIZE) : 1;
  if (seoPageNumber > totalPages) {
    tags.push(new MetaTag({ name: 'ROBOTS', content: 'NOINDEX', id: 'robots' }));
  }

  return tags;
};

// --- Main Component ---

const StoreReviewsMetadata = ({ storeId, seoPageNumber }) => {
  const emtContentOptions = {
    variables: { contentId: storeId, pageName: EMT_PAGE_NAMES.REVIEWS }
  };
  const reviewsOptions = getGraphQlReviewsOptions({
    pageType: PAGE_TYPE.REVIEWS,
    pageSize: REVIEWS_PAGE_SIZE,
    storeId,
    page: seoPageNumber
  });
  const storeSearchOptions = {
    variables: { storeSearchInput: storeId, pagesize: '1' }
  };

  const { data: emtData } = useDataModel('emtContent', emtContentOptions);
  const { data: reviewData } = useDataModel('storeReviews', reviewsOptions);
  const { data: storeData } = useDataModel('storeSearch', storeSearchOptions);

  const data = { emtData, reviewData, storeData };
  const props = { seoPageNumber, storeId };

  useHelmet('storeReviewsMetadata', { data, props, skipCleanup: true }, storeReviewsHandler, [storeId]);

  return null;
};

// --- Component Static Properties (dataModel, propTypes, etc.) ---

StoreReviewsMetadata.displayName = 'StoreReviewsMetadata';

StoreReviewsMetadata.dataModel = extend(
  emtContentModel,
  storeReviewsModel,
  storeSearchModel
);

StoreReviewsMetadata.propTypes = {
  storeId: PropTypes.string.isRequired,
  seoPageNumber: PropTypes.number.isRequired
};

export { StoreReviewsMetadata };